import { from, Observable } from "rxjs";
import { PageRequest } from "src/app/support/page-request";
import { Paginated } from "src/app/support/paginated";
import { rxRandomErrorDelayAsync } from "src/app/support/rx-random-error-delay";
import { map, switchMap } from "rxjs/operators";
import { find } from "lodash-es";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { UserNotify } from "src/app/support/notify-decorator";
import { uuid, UUID } from "src/app/support/uuid";
import {
  loadDemoStorage,
  writeDemoStorage
} from "src/app/support/demo-storage";
import { Injectable } from "@angular/core";
import { RevenueCenter } from "../models/revenue-center/revenue-center";
import { RevenueCenterCreate } from "../models/revenue-center/revenue-center.create";
import { RevenueCenterDelete } from "../models/revenue-center/revenue-center.delete";
import { RevenueCenterLoad } from "../models/revenue-center/revenue-center.load";
import { RevenueCenterUpdate } from "../models/revenue-center/revenue-center.update";

const DB_NAME = "revenue-center";

@Injectable()
export class RevenueCenterBackend {
  load(request: RevenueCenterLoad): Observable<RevenueCenter> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      map((list) => find(list, (x) => x.uuid === request.uuid)!)
    );
  }

  list(request: PageRequest): Observable<Paginated<RevenueCenter>> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      map(
        (list) =>
          <Paginated<RevenueCenter>>{
            list,
            count: list.length
          }
      )
    );
  }

  @UserNotify({
    successMessage: marker("Centro di ricavo creato"),
    errorMessage: marker("Impossibile creare il centro di ricavo")
  })
  create(request: RevenueCenterCreate): Observable<UUID> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      switchMap((list) => {
        const newUuid = uuid();
        const newRevenueCenter: Partial<RevenueCenter> = {
          uuid: newUuid,
          name: request.name
        };

        return from(
          writeDemoStorage(DB_NAME, [...list, newRevenueCenter])
        ).pipe(map(() => newUuid));
      })
    );
  }

  @UserNotify({
    successMessage: marker("Centro di ricavo aggiornato"),
    errorMessage: marker("Impossibile aggiornare il centro di ricavo")
  })
  update(request: RevenueCenterUpdate): Observable<void> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      switchMap((list) => {
        const newUuid = uuid();
        const newRevenueCenter: Partial<RevenueCenter> = {
          uuid: newUuid,
          name: request.name
        };

        return from(
          writeDemoStorage(
            DB_NAME,
            list.map((x) => {
              if (x.uuid === request.uuid) {
                return {
                  ...x,
                  ...request
                };
              }

              return x;
            })
          )
        );
      })
    );
  }

  @UserNotify({
    successMessage: marker("Centro  rimosso"),
    errorMessage: marker("Impossibile rimuovere il centro")
  })
  delete(request: RevenueCenterDelete): Observable<void> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      switchMap((list: RevenueCenter[]) => {
        return from(
          writeDemoStorage(
            DB_NAME,
            list.filter((x) => x.uuid !== request.uuid)
          )
        );
      })
    );
  }
}

async function loadDb(): Promise<RevenueCenter[]> {
  return (await loadDemoStorage<RevenueCenter[]>(DB_NAME)) || [];
}
